/**
 * ShopifyVariantSelector - Displays variant options (size, color) for Shopify products
 * 
 * Fetches product variants from Shopify and shows selectable options.
 * Handles the "Add to Bag" action with the selected variant.
 */
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Loader2, AlertCircle } from 'lucide-react';
import { useShopify } from '@/context/ShopifyContext';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';

// Size ordering map - smaller sizes first
const SIZE_ORDER = {
  'xxs': 1, '2xs': 1,
  'xs': 2,
  's': 3, 'small': 3,
  'm': 4, 'medium': 4,
  'l': 5, 'large': 5,
  'xl': 6,
  'xxl': 7, '2xl': 7,
  'xxxl': 8, '3xl': 8,
  '4xl': 9,
  '5xl': 10,
};

// Sort size values from smallest to largest
const sortSizeValues = (values, optionName) => {
  const lowerName = optionName.toLowerCase();
  // Only sort if this looks like a size option
  if (!lowerName.includes('size') && !['xs', 's', 'm', 'l', 'xl', 'xxl'].some(s => 
    values.some(v => v.value.toLowerCase() === s)
  )) {
    return values;
  }
  
  return [...values].sort((a, b) => {
    const aLower = a.value.toLowerCase().trim();
    const bLower = b.value.toLowerCase().trim();
    
    const aOrder = SIZE_ORDER[aLower];
    const bOrder = SIZE_ORDER[bLower];
    
    // If both have known sizes, sort by order
    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }
    // If only one has known size, put it first
    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;
    
    // Try numeric comparison (e.g., "32", "34", "36")
    const aNum = parseFloat(aLower);
    const bNum = parseFloat(bLower);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    
    // Fallback to alphabetical
    return aLower.localeCompare(bLower);
  });
};

const ShopifyVariantSelector = ({ productId, productTitle, displayPrice, inlineMode = false, inlineVariantsOnly = false, buttonsOnly = false, showDetailButton = false, detailUrl = '', groupId = '', styleTags = null, productPills = null, onColorChange = null, isSoldOut = false }) => {
  const { fetchProduct, addToCart, isEnabled } = useShopify();
  const { formatPrice, getCurrencySymbol } = useCurrency();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to track current selections (avoids stale state in async handlers)
  const selectedOptionsRef = useRef({});

  // Helper to format variant price with currency conversion
  const formatVariantPrice = (variant) => {
    if (!variant?.priceV2) return '';
    const amount = parseFloat(variant.priceV2.amount || 0);
    const currency = variant.priceV2.currencyCode || 'EUR';
    return formatPrice(amount, { fromCurrency: currency });
  };

  // Fetch product variants on mount
  useEffect(() => {
    const loadProduct = async () => {
      if (!productId || !isEnabled) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const fetchedProduct = await fetchProduct(productId);
        if (fetchedProduct) {
          setProduct(fetchedProduct);
          // Pre-select the first available variant
          const availableVariant = fetchedProduct.variants.find(v => v.available);
          if (availableVariant) {
            setSelectedVariant(availableVariant);
            // Set initial selected options based on first variant
            const initialOptions = {};
            availableVariant.selectedOptions?.forEach(opt => {
              initialOptions[opt.name] = opt.value;
              // Notify parent of initial color for variant images
              if (onColorChange && (opt.name.toLowerCase().includes('color') || opt.name.toLowerCase().includes('colour'))) {
                onColorChange(opt.value);
              }
            });
            selectedOptionsRef.current = initialOptions;
            setSelectedOptions(initialOptions);
          }
        } else {
          setError('Product not found in Shopify');
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product options');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, isEnabled, fetchProduct]);

  // Update selected variant when options change
  useEffect(() => {
    if (!product || Object.keys(selectedOptions).length === 0) return;

    // Find variant matching all selected options
    const matchingVariant = product.variants.find(variant => {
      return variant.selectedOptions?.every(
        opt => selectedOptions[opt.name] === opt.value
      );
    });

    if (matchingVariant) {
      setSelectedVariant(matchingVariant);
    }
  }, [selectedOptions, product]);

  // Handle option selection
  const handleOptionChange = (optionName, value) => {
    const newOptions = {
      ...selectedOptionsRef.current,
      [optionName]: value
    };
    selectedOptionsRef.current = newOptions;
    setSelectedOptions(newOptions);
    
    // Notify parent of color change for variant images
    if (onColorChange && (optionName.toLowerCase().includes('color') || optionName.toLowerCase().includes('colour'))) {
      onColorChange(value);
    }
  };

  // Handle Add to Bag
  const handleAddToCart = async () => {
    if (!product || adding) return;

    // Access ref's current value directly - this ensures we get the latest
    const currentOptions = { ...selectedOptionsRef.current };
    
    // Find the currently matching variant based on selectedOptions
    const currentVariant = product.variants.find(variant => {
      return variant.selectedOptions?.every(
        opt => currentOptions[opt.name] === opt.value
      );
    });

    if (!currentVariant) {
      toast.error('Please select all options');
      return;
    }

    setAdding(true);
    try {
      const result = await addToCart(currentVariant.id, 1);
      if (result.success) {
        toast.success('Added to your bag!');
      } else {
        toast.error(result.error || 'Failed to add to bag');
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      toast.error('Something went wrong');
    } finally {
      setAdding(false);
    }
  };

  // Loading state
  if (loading) {
    if (inlineMode) {
      return <Loader2 className="w-4 h-4 animate-spin text-purple-600 flex-shrink-0" />;
    }
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
        <span className="ml-2 text-sm text-gray-500">Loading options...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    if (inlineMode) return null;
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      </div>
    );
  }

  // No product found
  if (!product || !product.variants?.length) {
    if (inlineMode) return null;
    return (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 text-amber-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          Product not available
        </div>
      </div>
    );
  }

  // Get unique options (size, color, etc.)
  const options = product.options || [];
  const hasMultipleVariants = product.variants.length > 1;

  // INLINE VARIANTS ONLY MODE - render Color, Size, Price horizontally in a single row (no buttons)
  if (inlineVariantsOnly) {
    if (!hasMultipleVariants) {
      // Just show price if no variants
      return selectedVariant ? (
        <div>
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">PRICE:</h3>
          <span className="text-sm font-mono font-bold text-gray-900">
            {formatVariantPrice(selectedVariant)}
          </span>
        </div>
      ) : null;
    }
    
    return (
      <div className="flex flex-wrap gap-x-8 gap-y-3 items-end">
        {options.map(option => {
          const hasMultipleValues = option.values.length > 1;
          
          return (
            <div key={option.name}>
              <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">
                {option.name}:
              </h3>
              
              <div className="flex flex-wrap gap-1 items-center min-h-[28px]">
                {hasMultipleValues ? (
                  sortSizeValues(option.values, option.name).map(value => {
                    const isSelected = selectedOptions[option.name] === value.value;
                    const isAvailable = product.variants.some(v => 
                      v.available && v.selectedOptions?.some(
                        opt => opt.name === option.name && opt.value === value.value
                      )
                    );
                    
                    return (
                      <button
                        key={value.value}
                        onClick={() => handleOptionChange(option.name, value.value)}
                        disabled={!isAvailable}
                        className={`
                          px-2 py-1 text-xs font-mono border transition-all rounded
                          ${isSelected 
                            ? 'border-purple-600 bg-purple-50 text-purple-700' 
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }
                          ${!isAvailable ? 'opacity-40 cursor-not-allowed line-through' : 'cursor-pointer'}
                        `}
                        data-testid={`variant-option-${option.name}-${value.value}`}
                      >
                        {value.value}
                      </button>
                    );
                  })
                ) : (
                  <span className="px-2 py-1 text-xs font-mono border border-purple-600 bg-purple-50 text-purple-700 rounded">
                    {selectedOptions[option.name] || option.values[0]?.value}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Price column */}
        {selectedVariant && (
          <div>
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">PRICE:</h3>
            <div className="flex items-center min-h-[28px]">
              <span className="text-sm font-mono font-bold text-gray-900">
                {formatVariantPrice(selectedVariant)}
              </span>
              {!selectedVariant.available && (
                <span className="ml-2 text-xs text-red-500 font-medium">Out of Stock</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // BUTTONS ONLY MODE - render just the Add to Bag and See Item Details buttons
  if (buttonsOnly) {
    return (
      <div className={showDetailButton ? "flex gap-3 w-full" : "w-full"}>
        {/* Add to Bag Button */}
        {isSoldOut ? (
          <div
            className={`${showDetailButton ? 'flex-1' : 'w-full'} inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-400 text-white font-mono text-xs uppercase tracking-widest cursor-not-allowed`}
            data-testid="sold-out-btn"
          >
            Sold Out
          </div>
        ) : (
          <button
            onClick={handleAddToCart}
            disabled={!selectedVariant?.available || adding}
            className={`${showDetailButton ? 'flex-1' : 'w-full'} inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-mono text-xs uppercase tracking-widest hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid="add-to-bag-btn"
          >
            {adding ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Adding...
              </>
            ) : !selectedVariant?.available ? (
              'Out of Stock'
            ) : (
              <>
                <ShoppingBag className="w-4 h-4" />
                Add to Bag
              </>
            )}
          </button>
        )}
        
        {/* See Item Details Button */}
        {showDetailButton && detailUrl && (
          <Link 
            to={detailUrl} 
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white border-2 border-site-text text-site-text font-mono text-xs uppercase tracking-widest hover:bg-site-bg-alt transition-colors" 
            data-testid={`see-details-btn-${groupId}`}
          >
            See Item Details
          </Link>
        )}
      </div>
    );
  }

  // OLD INLINE MODE (kept for backwards compatibility) - render variant options as columns
  if (inlineMode) {
    if (!hasMultipleVariants) return null;
    
    return (
      <>
        {options.map(option => {
          const hasMultipleValues = option.values.length > 1;
          
          return (
            <div key={option.name}>
              <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">
                {option.name}:
              </h3>
              
              <div className="flex flex-wrap gap-1 items-center min-h-[28px]">
                {hasMultipleValues ? (
                  sortSizeValues(option.values, option.name).map(value => {
                    const isSelected = selectedOptions[option.name] === value.value;
                    const isAvailable = product.variants.some(v => 
                      v.available && v.selectedOptions?.some(
                        opt => opt.name === option.name && opt.value === value.value
                      )
                    );
                    
                    return (
                      <button
                        key={value.value}
                        onClick={() => handleOptionChange(option.name, value.value)}
                        disabled={!isAvailable}
                        className={`
                          px-2 py-1 text-xs font-mono border transition-all rounded
                          ${isSelected 
                            ? 'border-purple-600 bg-purple-50 text-purple-700' 
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }
                          ${!isAvailable ? 'opacity-40 cursor-not-allowed line-through' : 'cursor-pointer'}
                        `}
                        data-testid={`variant-option-${option.name}-${value.value}`}
                      >
                        {value.value}
                      </button>
                    );
                  })
                ) : (
                  <span className="px-2 py-1 text-xs font-mono border border-purple-600 bg-purple-50 text-purple-700 rounded">
                    {selectedOptions[option.name] || option.values[0]?.value}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Price column */}
        {selectedVariant && (
          <div>
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">
              Price:
            </h3>
            <div className="flex items-center min-h-[28px]">
              <span className="text-lg font-mono font-bold text-gray-900">
                {formatVariantPrice(selectedVariant)}
              </span>
              {!selectedVariant.available && (
                <span className="ml-2 text-xs text-red-500 font-medium">Out of Stock</span>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // FULL MODE - render variant options AND Add to Bag button
  return (
    <>
      {/* Row 1: Product pills + Variant options (size, color) + Price - all inline */}
      <div className="flex flex-wrap gap-x-8 gap-y-3 items-end mb-4">
        {/* Product pills slot */}
        {productPills}
        
        {/* Variant options (size, color) */}
        {hasMultipleVariants && options.map(option => {
          const hasMultipleValues = option.values.length > 1;
          
          return (
            <div key={option.name}>
              <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">
                {option.name}:
              </h3>
              
              <div className="flex flex-wrap gap-1 items-center min-h-[28px]">
                {hasMultipleValues ? (
                  sortSizeValues(option.values, option.name).map(value => {
                    const isSelected = selectedOptions[option.name] === value.value;
                    const isAvailable = product.variants.some(v => 
                      v.available && v.selectedOptions?.some(
                        opt => opt.name === option.name && opt.value === value.value
                      )
                    );
                    
                    return (
                      <button
                        key={value.value}
                        onClick={() => handleOptionChange(option.name, value.value)}
                        disabled={!isAvailable}
                        className={`
                          px-2 py-1 text-xs font-mono border transition-all rounded
                          ${isSelected 
                            ? 'border-purple-600 bg-purple-50 text-purple-700' 
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }
                          ${!isAvailable ? 'opacity-40 cursor-not-allowed line-through' : 'cursor-pointer'}
                        `}
                        data-testid={`variant-option-${option.name}-${value.value}`}
                      >
                        {value.value}
                      </button>
                    );
                  })
                ) : (
                  <span className="px-2 py-1 text-xs font-mono border border-purple-600 bg-purple-50 text-purple-700 rounded">
                    {selectedOptions[option.name] || option.values[0]?.value}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        
        {/* Price - inline with variants */}
        {selectedVariant && (
          <div>
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">Price:</h3>
            <div className="flex items-center min-h-[28px]">
              <span className="text-lg font-mono font-bold text-gray-900">
                {formatVariantPrice(selectedVariant)}
              </span>
              {!selectedVariant.available && (
                <span className="ml-2 text-xs text-red-500 font-medium">Out of Stock</span>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Row 2: Style tags slot */}
      {styleTags}
      
      {/* Row 4: Buttons */}
      <div className={showDetailButton ? "flex gap-3 w-full" : "w-full"}>
        {/* Add to Bag Button */}
        {isSoldOut ? (
          <div
            className={`${showDetailButton ? 'flex-1' : 'w-full'} inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-400 text-white font-mono text-xs uppercase tracking-widest cursor-not-allowed`}
            data-testid="sold-out-btn"
          >
            Sold Out
          </div>
        ) : (
          <button
            onClick={handleAddToCart}
            disabled={!selectedVariant?.available || adding}
            className={`${showDetailButton ? 'flex-1' : 'w-full'} inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-mono text-xs uppercase tracking-widest hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid="add-to-bag-btn"
          >
            {adding ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Adding...
              </>
            ) : !selectedVariant?.available ? (
              'Out of Stock'
            ) : (
              <>
                <ShoppingBag className="w-4 h-4" />
                Add to Bag
              </>
            )}
          </button>
        )}
        
        {/* See Item Details Button - only when showDetailButton is true */}
        {showDetailButton && detailUrl && (
          <Link 
            to={detailUrl} 
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white border-2 border-site-text text-site-text font-mono text-xs uppercase tracking-widest hover:bg-site-bg-alt transition-colors" 
            data-testid={`see-details-btn-${groupId}`}
          >
            See Item Details
          </Link>
        )}
      </div>
    </>
  );
};

export default ShopifyVariantSelector;
